import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';

import { Booth, DesignElementKind } from 'models/cms/booths';
import { selectLocale } from 'models/localization';
import { useTranslation } from 'react-i18next';
import { FETCH_ASSETS_URL } from 'appenv';

import findAvailableLocale from 'models/helpers/findAvailableLocale';
import BoothContainer from '../../components/BoothContainer';
import GenericCMSBoothBanner from '../../components/GenericCMSBoothBanner';
import BusinessPopover from './BusinessPopover';
import useEmailContactsMemo from '../../useEmailContactsMemo';

const flowerImg = `${FETCH_ASSETS_URL}/assets/booths/bta1014/flower.png`;
const boothTableImg = `${FETCH_ASSETS_URL}/assets/booths/bta1014/booth_table.svg`;
const backgroundImg = `${FETCH_ASSETS_URL}/assets/booths/bta1014/background_v2.png`;

const TopBannerContainer = styled.div`
  position: absolute;
  width: 740px;
  height: 46px;
  left: calc((100% - 740px) / 2);
  top: 83px;
  line-height: 45px;
  text-align: center;
  font-size: 32px;
  text-transform: uppercase;
  color: #333;
  z-index: 1;
`;

const BackgroundContainer = styled.div`
  width: 1067px;
  height: 776px;
  position: absolute;
  top: -50px;
  background: url(${backgroundImg});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  left: calc((100% - 1067px) / 2);
`;

const RepresentativeContainer = styled.div<{ representativeImg: string }>`
  background: url(${({ representativeImg }) => representativeImg});
  position: absolute;
  z-index: 1;
  height: 224px;
  width: 108px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  bottom: -13px;
  left: 285px;
`;

const LeftBannerContainer = styled.div`
  height: 226px;
  width: 135px;
  position: absolute;
  left: -57px;
  bottom: -71px;
  z-index: 1;
`;

const DisplayBoardContainer = styled.div`
  height: 74px;
  width: 127px;
  left: 109px;
  top: 309px;
  position: absolute;
  z-index: 1;
`;

const BottomBannerContainer = styled.div`
  height: 80px;
  width: 148px;
  left: 273px;
  bottom: -9px;
  position: absolute;
  z-index: 1;
`;

const MiddleBannerContainer = styled.div`
  height: 202px;
  width: 252px;
  right: 211px;
  bottom: 94px;
  position: absolute;
  z-index: 1;
`;

const RightBannerContainer = styled.div`
  height: 280px;
  width: 106px;
  position: absolute;
  right: 74px;
  bottom: -22px;
  z-index: 1;
`;

const FlowContainer = styled.img`
  width: 71px;
  height: 125px;
  position: absolute;
  z-index: 2;
  pointer-events: none;
  right: 125px;
  bottom: -45px;
`;

const BrochureContainer = styled.div<{ enableClick: boolean }>`
  bottom: -28px;
  left: 101px;
  width: 66px;
  height: 190px;
  cursor: ${({ enableClick }) => (enableClick ? 'pointer' : 'initial')};
  position: absolute;
  background: url(https://ems-assets.eventxcdn.com/ckeditor/pictures/data/000/002/746/content/shelf-.png);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center center;
  z-index: 1;
`;

const BottomTable = styled.div`
  width: 385px;
  background: url(${boothTableImg});
  position: absolute;
  z-index: 1;
  height: 200px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
  bottom: -60px;
  left: 132px;
`;

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 900px;
`;

const ViewMoreBtnContainer = styled.div`
  margin: -6px auto;
  width: fit-content;
  background-color: orange;
  color: #FFF;
  border: 1px solid #FFF;
  padding: 0 4px;
  font-size: 8px;
  height: 16px;
  line-height: 16px;
  cursor: pointer;
`;

const BusinessPopoverContainer = styled.div`
  position: absolute;
  top: 277px;
  left: 249px;
  min-width: 177px;
  z-index: 2;
`;

const BubbleArrow = styled.div`
  height: 20px;
  width: 20px;
  position: absolute;
  bottom: -8px;
  left: 50%;
  background: #CECECE;
  border-radius: 2px;
  transform: rotate(45deg) translate(-50%, 8px);
`;

export interface BannerProps {
  booth: Booth;
  handleClick: (type: string, isCMSElement: boolean) => void;
  isPreview?: boolean;
}

const Banner: React.FC<BannerProps> = ({
  booth,
  handleClick = () => null,
  isPreview = false,
}: BannerProps) => {
  const {
    leftBanner, rightBanner, displayBoard, middleBanner, bottomBanner, representative,
  } = useMemo(() => {
    const elements = (booth.designElements || []);
    return ({
      leftBanner: elements.find((designNode) => designNode.name === 'left_banner'),
      representative: elements.find((designNode) => designNode.name === 'representative'),
      rightBanner: elements.find((designNode) => designNode.name === 'right_banner'),
      displayBoard: elements.find((designNode) => designNode.name === 'display_board'),
      middleBanner: elements.find((designNode) => designNode.name === 'middle_banner'),
      bottomBanner: elements.find((designNode) => designNode.name === 'bottom_banner'),
    });
  }, [booth.designElements]);
  const showBusinessPopover = useMemo(() => !isPreview, [isPreview]);
  const locale = useSelector(selectLocale);
  const { t } = useTranslation();
  const tabs = useMemo(() => booth?.rootContentElement?.tabs || [], [booth?.rootContentElement?.tabs]);
  const emailContacts = useEmailContactsMemo(tabs);

  const hasMultipleVideo = useMemo(() => (booth.designElements || []).filter((designNode) => designNode?.kind === DesignElementKind.Video).length > 1, [booth.designElements]);

  return (
    <RootContainer>
      <BoothContainer needOffset={!isPreview}>
        <TopBannerContainer>
          {booth.name[locale]}
        </TopBannerContainer>
        <BackgroundContainer />
        {
          leftBanner && (
            <LeftBannerContainer onClick={() => handleClick(leftBanner.name, true)}>
              <GenericCMSBoothBanner
                bannerData={leftBanner}
                autoPlayVideo={!isPreview && !hasMultipleVideo}
                onBannerClick={handleClick as any}
              />
            </LeftBannerContainer>
          )
        }
        {
          displayBoard && (
            <DisplayBoardContainer onClick={() => handleClick(displayBoard.name, true)}>
              <GenericCMSBoothBanner
                bannerData={displayBoard}
                autoPlayVideo={!isPreview && !hasMultipleVideo}
                onBannerClick={handleClick as any}
              />
            </DisplayBoardContainer>
          )
        }
        <BrochureContainer enableClick onClick={() => handleClick('attachment', false)} />
        <RepresentativeContainer representativeImg={findAvailableLocale(representative?.image, locale)?.originalImageUrl} />
        <BottomTable />
        {
          bottomBanner && (
            <BottomBannerContainer onClick={() => handleClick(bottomBanner.name, true)}>
              <GenericCMSBoothBanner
                bannerData={bottomBanner}
                autoPlayVideo={!isPreview && !hasMultipleVideo}
                onBannerClick={handleClick as any}
              />
            </BottomBannerContainer>
          )
        }
        {
          showBusinessPopover && (
            <BusinessPopoverContainer>
              <BusinessPopover
                boothId={booth.id}
                boothName={booth.name[locale]}
                emailContacts={emailContacts}
                meetupUrl={booth.meetupEnabled ? booth.appointmentBookingUrl : ''}
              />
              <BubbleArrow />
            </BusinessPopoverContainer>
          )
        }
        {
          middleBanner && (
            <MiddleBannerContainer onClick={() => handleClick(middleBanner.name, true)}>
              <GenericCMSBoothBanner
                bannerData={middleBanner}
                autoPlayVideo={!isPreview}
                onBannerClick={handleClick as any}
              />
              {
                middleBanner.kind === DesignElementKind.Richtext && (
                  <ViewMoreBtnContainer onClick={() => handleClick('desk', false)}>
                    {t('booth.view_more', 'VIEW MORE')}
                  </ViewMoreBtnContainer>
                )
              }
            </MiddleBannerContainer>
          )
        }
        {
          rightBanner && (
            <RightBannerContainer onClick={() => handleClick(rightBanner.name, true)}>
              <GenericCMSBoothBanner
                bannerData={rightBanner}
                autoPlayVideo={!isPreview && !hasMultipleVideo}
                onBannerClick={handleClick as any}
              />
            </RightBannerContainer>
          )
        }
        <FlowContainer src={flowerImg} />
      </BoothContainer>
    </RootContainer>
  );
};

export default Banner;
